import { API_BASE_URL } from "src/config/api";
import React, { useState } from 'react';
import { toast } from 'react-toastify';
import {
  Link,
  Stack,
  TextField,
  Checkbox,
  Button,
  Typography,
  IconButton,
  Grid,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Container,
  Paper,
} from '@mui/material';
import DoneIcon from '@mui/icons-material/Done';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/material.css';

export default function SignupForm() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    fullName: '',
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
    phoneNumber: '',
    referralCode: '',
    agreeTerms: false,
  });

  const [errors, setErrors] = useState({
    fullName: '',
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
    phoneNumber: '',
    referralCode: '',
    agreeTerms: '',
    message: '',
  });

  const [passwordVisible, setPasswordVisible] = useState(false);
  const [confirmPasswordVisible, setConfirmPasswordVisible] = useState(false);
  const [isDialogOpen, setDialogOpen] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const [otpValue, setOtpValue] = useState('');
  const [otpError, setOtpError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isResendLoading, setIsResendLoading] = useState(false);
  const [isVerifiedLoading, setIsVerifiedLoading] = useState(false);
  const [emailVerificationError, setEmailVerificationError] = useState(true);

  const handleOTPChange = (event) => {
    const otp = event.target.value;
    setOtpValue(otp);
    setOtpError('');
  };

  const handleChange = (event) => {
    const { name, value, checked, type } = event.target;
    const newValue = type === 'checkbox' ? checked : value;

    setFormData((prevData) => ({
      ...prevData,
      [name]: newValue,
    }));
  };

  const handlePasswordVisibilityToggle = () => {
    setPasswordVisible((prevState) => !prevState);
  };

  const handleConfirmPasswordVisibilityToggle = () => {
    setConfirmPasswordVisible((prevState) => !prevState);
  };

  const validateOTP = () => {
    if (!otpValue) {
      setOtpError('OTP is required');
    } else if (!/^\d{4}$/.test(otpValue)) {
      setOtpError('OTP must be exactly four digits');
    } else {
      setOtpError('');
      verifyOTP();
    }
  };

  const registerUser = async () => {
    setIsLoading(true);
    const payload = {
      fullName: formData.fullName,
      userName: formData.username,
      email: formData.email.toLowerCase(),
      password: formData.password,
      phoneNumber: formData.phoneNumber,
      referralCode: formData.referralCode,
    };

    try {
      const registerResponse = await axios.post(`${API_BASE_URL}/register`, payload);

      if (registerResponse.status === 200) {
        toast.success(registerResponse.data.message, { autoClose: 2000 });
        closeDialog();
        navigate('/login');
      } else {
        console.error('Registration failed:', registerResponse.data.message);
        setErrors((prevErrors) => ({ ...prevErrors, message: registerResponse.data.message }));
      }
    } catch (registerError) {
      if (registerError.response) {
        toast.error(registerError.response.data.message, { autoClose: 2000 });
      } else if (registerError.request) {
        setErrors((prevErrors) => ({ ...prevErrors, message: 'No response from the server' }));
      } else {
        setErrors((prevErrors) => ({ ...prevErrors, message: 'An error occurred while sending the request' }));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const resendOTP = async () => {
    try {
      setIsResendLoading(true);
      const response = await fetch(`${API_BASE_URL}/sendEmailVerifactionMail`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ emailId: formData.email.toLowerCase() }),
      });

      const data = await response.json();

      if (data.success === true) {
        toast.success(data.message, { autoClose: 2000 });
        openDialog();
      } else {
        toast.error(data.message);
        console.error('Failed to resend OTP');
        closeDialog();
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setIsResendLoading(false);
    }
  };

  const verifyOTP = async () => {
    try {
      setIsVerifiedLoading(true);
      const response = await fetch(`${API_BASE_URL}/verifyOTP`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ otp: otpValue }),
      });

      const data = await response.json();

      if (data.success === true) {
        toast.success(data.message, { autoClose: 2000 });
        setIsVerified(true);
        setDialogOpen(false);
        setEmailVerificationError(false);
      } else {
        toast.error(data.message);
        setEmailVerificationError(true);
      }
    } catch (error) {
      console.error('Error verifying OTP:', error);
      toast.error('An error occurred while verifying OTP. Please try again later.');
    } finally {
      setIsVerifiedLoading(false);
    }
  };

  const openDialog = () => {
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
  };

  const sendOTP = async () => {
    try {

      if (!formData.email) {
        toast.error('Please enter your email address.');
        return;
      }
      setIsVerifiedLoading(true);

      const response = await fetch(`${API_BASE_URL}/sendEmailVerifactionMail`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ emailId: formData.email.toLowerCase() }),
      });

      const data = await response.json();
      console.log("data",data)
      if (data.success === true) {
        toast.success(data.message, { autoClose: 2000 });
        setDialogOpen(true);
      } else {
        console.error('Failed to send OTP');
        toast.error(data.message || 'Failed to send OTP');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error sending OTP. Please try again later.');
    } finally {
      setIsVerifiedLoading(false);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setErrors({});
    setOtpError('');

    const validationErrors = {};

    if (emailVerificationError) {
      toast.error('Please verify your email before proceeding');
      return;
    }
    if (!formData.agreeTerms) {
      validationErrors.agreeTerms = 'You must agree to the terms and conditions';
      return;
    }

    if (Object.keys(validationErrors).length === 0) {
      if (isVerified) {
        registerUser();
      }
    } else {
      setErrors(validationErrors);
    }
  };


  return (
    <>
      <form onSubmit={handleSubmit} disabled={isLoading}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <TextField
              name="fullName"
              label="Full Name"
              variant="outlined"
              fullWidth
              required
              value={formData.fullName}
              onChange={(e) => {
                handleChange(e);
                e.target.reportValidity();
              }}
              inputProps={{
                minLength: 3,
                maxLength: 50,
                pattern: "^[A-Za-z\\s]+$",
                title: "Only letters and spaces. 3-50 characters.",
              }}
            />
          </Grid>

          {/* Username */}
          <Grid item xs={12}>
            <TextField
              name="username"
              label="Username"
              variant="outlined"
              fullWidth
              required
              value={formData.username}
              onChange={(e) => {
                handleChange(e);
                e.target.reportValidity();
              }}
              inputProps={{
                pattern: "^[a-z0-9]+$",
                title: "Lowercase letters and numbers only.",
              }}
            />
          </Grid>

          {/* Email */}
          <Grid item xs={12}>
            <TextField
              name="email"
              label="Email Address"
              variant="outlined"
              type='email'
              fullWidth
              required
              value={formData.email}
              onChange={(e) => {
                handleChange(e);
                e.target.reportValidity();
              }}
              disabled={isVerified}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    {isVerified ? (
                      <DoneIcon style={{ color: 'green' }} />
                    ) : isVerifiedLoading ? (
                      <CircularProgress size={20} />
                    ) : (
                      <Button type="button" onClick={sendOTP}>
                        Verify
                      </Button>
                    )}
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          {/* Password */}
          <Grid item xs={12} sm={6}>
            <TextField
              name="password"
              label="Password"
              variant="outlined"
              fullWidth
              required
              disabled={!isVerified}
              type={passwordVisible ? 'text' : 'password'}
              value={formData.password}
              onChange={(e) => {
                handleChange(e);
                e.target.reportValidity();
              }}
              inputProps={{
                minLength: 8,
                maxLength: 20,
                pattern: "(?=.*[a-z])(?=.*[A-Z])(?=.*\\d)(?=.*[@#$%^&+=]).{8,20}",
                title: "8–20 chars, include upper, lower, digit & symbol.",
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={handlePasswordVisibilityToggle} edge="end">
                      {passwordVisible ? <Visibility /> : <VisibilityOff />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          {/* Confirm Password */}
          <Grid item xs={12} sm={6}>
            <TextField
              name="confirmPassword"
              label="Confirm Password"
              variant="outlined"
              fullWidth
              required
              disabled={!isVerified}
              type={confirmPasswordVisible ? 'text' : 'password'}
              value={formData.confirmPassword}
              onChange={(e) => {
                handleChange(e);
                e.target.reportValidity();
              }}
              inputProps={{
                pattern: formData.password,
                title: "Make sure both passwords match exactly.",
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={handleConfirmPasswordVisibilityToggle} edge="end">
                      {confirmPasswordVisible ? <Visibility /> : <VisibilityOff />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          {/* Phone Number */}
          <Grid item xs={12}>
            <PhoneInput
              country={'in'} // default country
              value={formData.phoneNumber}
              onChange={(value, data, event, formattedValue) => {
                setFormData((prev) => ({
                  ...prev,
                  phoneNumber: value, // full international value like +919876543210
                  countryCode: data.dialCode,
                }));
              }}
              inputProps={{
                name: 'phoneNumber',
                disabled: !isVerified,
              }}
              inputStyle={{ width: '100%' }}
            />
          </Grid>

          {/* Referral Code */}
          <Grid item xs={12}>
            <TextField
              name="referralCode"
              label="Referral Code"
              variant="outlined"
              fullWidth
              disabled={!isVerified}
              value={formData.referralCode}
              onChange={handleChange}
              inputProps={{
                maxLength: 8,
                pattern: "^.{8}$", // exactly 8 characters
                title: "Referral code must be exactly 8 characters if provided.",
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <Stack direction="row" alignItems="center">
              <Checkbox name="agreeTerms" checked={formData.agreeTerms} onChange={handleChange} disabled={!isVerified} color="primary" />
              <Typography variant="body2">
                I agree to the{' '}
                <Link href="terms-and-conditions" target="_blank">
                  Terms and Conditions
                </Link>
              </Typography>
            </Stack>
            <Typography variant="body2" color="error">
              {errors.agreeTerms}
            </Typography>
          </Grid>
          {errors.message && (
            <Typography variant="body2" color="error" sx={{ mb: 2 }}>
              {errors.message}
            </Typography>
          )}
          <Grid item xs={12}>
            <div style={{ display: 'flex', justifyContent: 'center', marginTop: '1rem' }}>
              <Button type="submit" variant="contained" color="primary" fullWidth disabled={isLoading || !isVerified}>
                {isLoading ? <CircularProgress size={20} sx={{ color: 'white' }} /> : 'Sign Up'}
              </Button>
            </div>
          </Grid>
        </Grid >
      </form >
      <Container maxWidth="md">
        <Dialog open={isDialogOpen} onClose={closeDialog} PaperComponent={Paper}>
          <div><DialogTitle>OTP Verification</DialogTitle></div>
          <DialogContent>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12}>
                <TextField
                  name="otp"
                  label="OTP"
                  variant="outlined"
                  autoComplete="off"
                  fullWidth
                  value={otpValue}
                  onChange={handleOTPChange}
                  error={Boolean(otpError)}
                  helperText={otpError}
                />
              </Grid>
              <Grid item xs={12}>
                <div style={{ display: 'flex', justifyContent: 'center', marginTop: '0rem' }}>
                  {isVerifiedLoading ? (
                    <CircularProgress />
                  ) : (
                    <>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={validateOTP}
                        startIcon={<CheckCircleIcon />}
                        endIcon={<ErrorIcon />}
                        disabled={isVerifiedLoading}
                      >
                        Verify OTP
                      </Button>
                    </>
                  )}
                </div>
              </Grid>
            </Grid>

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '1rem' }}>
              <DialogActions>
                {isResendLoading ? (
                  <CircularProgress />
                ) : (
                  <Button color="primary" onClick={resendOTP}>
                    Resend OTP
                  </Button>
                )}
                <Button onClick={closeDialog} color="primary">
                  Close
                </Button>
              </DialogActions>
            </div>
          </DialogContent>
        </Dialog>
      </Container>
    </>
  );
}
